import React from 'react';
import {
Box,
Paper,
Typography,
Divider,
List,
ListItem,
ListItemIcon,
ListItemText
} from '@mui/material';
import {
    PieChart,
    DateRange,
    CalendarToday,
    Business,
    Group,
    Storage,
    BarChart
} from '@mui/icons-material';
import {
    ResponsiveContainer,
    BarChart as RechartsBarChart,
    Bar,
    XAxis,
    YAxis,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    Tooltip,
    Legend
} from 'recharts';
import { DashboardData } from '../../types';
import {
    COLORS,
    calculateDataStats,
    getOccupationData,
    getTimingData,
    getResultData,
    getDepartmentContentData,
    generateContentColors
} from './utils/dataProcessing';
import {
renderOccupationLegend,
renderTimingLegend,
renderResultLegend,
CategoryAxisTick,
getOverviewCaptureChartHeight,
getOverviewCaptureLegendHeight,
getOverviewLegendHeight,
useCategoryAxisLayout
} from './utils/chartUtils';

const DEPARTMENT_RESULT_TYPES = [
  'アルコールで実施した',
  '石鹸と流水で実施した',
  '実施しなかった',
] as const;

export const DEPARTMENT_CAPTURE_ROWS_PER_IMAGE = 75;

export const getDepartmentCapturePageCount = (
  data: DashboardData | undefined,
): number => {
  if (!data) {
    return 1;
  }
  return Math.max(
    1,
    Math.ceil(
      getDepartmentContentData(data).length /
        DEPARTMENT_CAPTURE_ROWS_PER_IMAGE,
    ),
  );
};

const COMPACT_PIE_LABEL_CENTER_THRESHOLD = 180;
const COMPACT_PIE_LABEL_CHARACTERS_PER_LINE = 3;

interface DepartmentCountLabelProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string | boolean | null;
}

export const getDepartmentCountLabelRightMargin = (maxValue: number): number =>
  Math.max(64, Array.from(`${maxValue}件`).length * 14 + 12);

export const getPieLabelLines = (name: string, centerX: number): string[] => {
  if (centerX >= COMPACT_PIE_LABEL_CENTER_THRESHOLD) {
    return [name];
  }

  const characters = Array.from(name);
  return Array.from(
    {
      length: Math.ceil(
        characters.length / COMPACT_PIE_LABEL_CHARACTERS_PER_LINE,
      ),
    },
    (_, index) =>
      characters
        .slice(
          index * COMPACT_PIE_LABEL_CHARACTERS_PER_LINE,
          (index + 1) * COMPACT_PIE_LABEL_CHARACTERS_PER_LINE,
        )
        .join(''),
  );
};

export const renderDepartmentCountLabel = ({
  x,
  y,
  width,
  height,
  value,
}: DepartmentCountLabelProps): React.ReactElement | null => {
  const hasValidValue =
    (typeof value === 'number' && Number.isFinite(value)) ||
    (typeof value === 'string' && value.length > 0);

  if (
    typeof x !== 'number' ||
    !Number.isFinite(x) ||
    typeof y !== 'number' ||
    !Number.isFinite(y) ||
    typeof width !== 'number' ||
    !Number.isFinite(width) ||
    width < 0 ||
    typeof height !== 'number' ||
    !Number.isFinite(height) ||
    height < 0 ||
    !hasValidValue
  ) {
    return null;
  }

  return (
    <text
      x={x + width + 8}
      y={y + height / 2}
      fill="#000"
      textAnchor="start"
      dominantBaseline="middle"
      fontSize={14}
    >
      {value}件
    </text>
  );
};

// ラベルと線を一緒に描画する新しいカスタムコンポーネント
const LabelWithLine = (props: any) => {
  const { cx, cy, midAngle, outerRadius, name, percent, fill } = props;

  // 5%未満のスライスにはラベルも線も表示しない
  if (percent < 0.05) {
    return null;
  }

  const RADIAN = Math.PI / 180;
  const isCompactLabel =
    typeof cx === 'number' && cx < COMPACT_PIE_LABEL_CENTER_THRESHOLD;
  const lineExtension = isCompactLabel ? 8 : 20;
  const textOffset = isCompactLabel ? 4 : 6;
  const labelLines = getPieLabelLines(String(name ?? ''), cx);

  // 線の始点 (円周上)
  const sx = cx + (outerRadius) * Math.cos(-midAngle * RADIAN);
  const sy = cy + (outerRadius) * Math.sin(-midAngle * RADIAN);

  // 線の終点
  const mx = cx + (outerRadius + lineExtension) * Math.cos(-midAngle * RADIAN);
  const my = cy + (outerRadius + lineExtension) * Math.sin(-midAngle * RADIAN);

  // ラベルテキストの位置
  const ex = mx + (Math.cos(-midAngle * RADIAN) >= 0 ? 1 : -1) * textOffset;
  const ey = my;

  const textAnchor = Math.cos(-midAngle * RADIAN) >= 0 ? 'start' : 'end';

  return (
    <g>
      {/* 線 (スライスの色) */}
      <path d={`M${sx},${sy}L${mx},${ey}`} stroke={fill} fill="none" />

      {/* ラベルテキスト (スライスの色) */}
      <text x={ex} y={ey} textAnchor={textAnchor} fill={fill} dominantBaseline="central" style={{ fontSize: '14px'}}>
        {isCompactLabel
          ? labelLines.map((line, index) => (
            <tspan
              key={`pie-label-line-${index}`}
              x={ex}
              dy={index === 0 ? `${-(labelLines.length - 1) * 0.55}em` : '1.1em'}
            >
              {line}
            </tspan>
          ))
          : name}
      </text>
    </g>
  );
};

interface OverviewTabProps {
data: DashboardData;
isChartCaptureMode?: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  data,
  isChartCaptureMode = false,
}) => {
// データ統計を計算
const dataStats = calculateDataStats(data);

// 職種データの取得
const occupationData = getOccupationData(data);
const occupationLegendColumns = occupationData.length >= 6 ? 2 : 1;
const occupationCaptureLegendHeight = getOverviewCaptureLegendHeight(
  occupationData.length,
  occupationLegendColumns,
);
const occupationLegendHeight = isChartCaptureMode
  ? occupationCaptureLegendHeight
  : getOverviewLegendHeight(occupationData.length, occupationLegendColumns);
const occupationChartHeight = isChartCaptureMode
  ? getOverviewCaptureChartHeight(occupationCaptureLegendHeight)
  : 460;

// 総件数を計算（割合表示用）
const totalJobCount = occupationData.reduce((sum, item) => sum + item.value, 0);

// タイミングデータの取得
const timingData = getTimingData(data);
const timingCaptureLegendHeight = getOverviewCaptureLegendHeight(timingData.length);
const timingLegendHeight = isChartCaptureMode
  ? timingCaptureLegendHeight
  : getOverviewLegendHeight(timingData.length);
const timingChartHeight = isChartCaptureMode
  ? getOverviewCaptureChartHeight(timingCaptureLegendHeight)
  : 460;

// 総件数を計算（割合表示用）
const totalTimingCount = timingData.reduce((sum, item) => sum + item.value, 0);

// 部署ごとの結果データの取得
const departmentContentData = getDepartmentContentData(data);
const departmentBarData = departmentContentData
  .map((department) => ({
    name: department.name,
    value: DEPARTMENT_RESULT_TYPES.reduce(
      (sum, resultType) => sum + (department[resultType] || 0),
      0,
    ),
  }))
  .sort((a, b) => b.value - a.value);
const maximumDepartmentCount = departmentBarData.reduce(
  (maximum, department) => Math.max(maximum, department.value),
  0,
);
const departmentCountLabelRightMargin =
  getDepartmentCountLabelRightMargin(maximumDepartmentCount);
const departmentAxisLayout = useCategoryAxisLayout(
  departmentContentData.map((department) => department.name),
);
const departmentChartPages = isChartCaptureMode
  ? Array.from(
      { length: getDepartmentCapturePageCount(data) },
      (_, pageIndex) =>
        departmentBarData.slice(
          pageIndex * DEPARTMENT_CAPTURE_ROWS_PER_IMAGE,
          (pageIndex + 1) * DEPARTMENT_CAPTURE_ROWS_PER_IMAGE,
        ),
    )
  : [departmentBarData];

// 結果データの取得
const resultData = getResultData(data);
const resultCaptureLegendHeight = getOverviewCaptureLegendHeight(resultData.length);
const resultLegendHeight = isChartCaptureMode
  ? resultCaptureLegendHeight
  : getOverviewLegendHeight(resultData.length);
const resultChartHeight = isChartCaptureMode
  ? getOverviewCaptureChartHeight(resultCaptureLegendHeight)
  : 460;

// 内容タイプの色を取得
const contentColorMap = generateContentColors(data);

return (
    <Box sx={{ width: '100%' }}>
    {/* 一段目：左にデータ概要、右に職種 */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
        {/* 左: データ概要 */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
        <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <DateRange sx={{ mr: 1 }} /> データ概要
            </Typography>
            <List>
            <ListItem>
                <ListItemIcon>
                <CalendarToday />
                </ListItemIcon>
                <ListItemText
                primary="期間"
                secondary={dataStats.collectionPeriod
                    ? `${dataStats.collectionPeriod.start.toLocaleDateString('ja-JP')} 〜 ${dataStats.collectionPeriod.end.toLocaleDateString('ja-JP')}`
                    : "データなし"
                }
                />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
                <ListItemIcon>
                <DateRange />
                </ListItemIcon>
                <ListItemText
                primary="実施日数"
                secondary={`${dataStats.uniqueDays}日`}
                />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
                <ListItemIcon>
                <Business />
                </ListItemIcon>
                <ListItemText
                primary="部署数"
                secondary={`${dataStats.uniqueDepartments}部署`}
                />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
                <ListItemIcon>
                <Group />
                </ListItemIcon>
                <ListItemText
                primary="職種数"
                secondary={`${occupationData.length}職種`}
                />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
                <ListItemIcon>
                <Storage />
                </ListItemIcon>
                <ListItemText
                primary="データ数"
                secondary={`${data.total_records}件`}
                />
            </ListItem>
            </List>
        </Paper>
        </Box>

        {/* 職種 */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
        <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PieChart sx={{ mr: 1 }} /> 職種
            </Typography>
            <Box sx={{ height: occupationChartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart margin={{ top: 30, right: 30, bottom: 20, left: 30 }}>
                <Pie
                    data={occupationData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    isAnimationActive={false}
                    labelLine={false}
                    label={<LabelWithLine />}
                    // 時計回りに表示
                    startAngle={90}
                    endAngle={-270}
                >
                    {occupationData.map((entry, index) => (
                    <Cell
                        key={`job-cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                    />
                    ))}
                </Pie>

                <Tooltip />
                {/* 職種用のカスタム凡例を使用 */}
                <Legend
                    verticalAlign="bottom"
                    align="center"
                    height={occupationLegendHeight}
                    content={renderOccupationLegend(occupationData, totalJobCount, isChartCaptureMode)}
                />
                </RechartsPieChart>
            </ResponsiveContainer>
            </Box>
        </Paper>
        </Box>
    </Box>

    <Box sx={{ width: '100%' }}>
        {/* 上段の2カラムレイアウト */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
        {/* 観察タイミング */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PieChart sx={{ mr: 1 }} /> 観察タイミング
            </Typography>
            <Box sx={{ height: timingChartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart margin={{ top: 30, right: 30, bottom: 20, left: 30 }}>
                    <Pie
                    data={timingData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    isAnimationActive={false}
                    labelLine={false}
                    label={<LabelWithLine />}
                    // 時計回りに表示
                    startAngle={90}
                    endAngle={-270}
                    >
                    {timingData.map((entry, index) => (
                        <Cell
                        key={`timing-cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        />
                    ))}
                    </Pie>

                    <Tooltip />
                    {/* 観察タイミング用のカスタム凡例を使用 */}
                    <Legend
                    verticalAlign="bottom"
                    align="center"
                    height={timingLegendHeight}
                    content={renderTimingLegend(timingData, totalTimingCount, isChartCaptureMode)}
                    />
                </RechartsPieChart>
                </ResponsiveContainer>
            </Box>
            </Paper>
        </Box>

        {/* 結果 */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PieChart sx={{ mr: 1 }} /> 結果
            </Typography>
            <Box sx={{ height: resultChartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart margin={{ top: 30, right: 30, bottom: 20, left: 30 }}>
                    <Pie
                    data={resultData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    isAnimationActive={false}
                    labelLine={false}
                    label={<LabelWithLine />}
                    // 時計回りに表示（件数の多い順）
                    startAngle={90}
                    endAngle={-270}
                    >
                    {resultData.map((item, index) => (
                        <Cell
                        key={`result-cell-${index}`}
                        fill={contentColorMap[item.name] || COLORS[index % COLORS.length]}
                        />
                    ))}
                    </Pie>
                    <Tooltip />
                    {/* 結果用のカスタム凡例を使用 */}
                    <Legend
                    verticalAlign="bottom"
                    align="center"
                    height={resultLegendHeight}
                    content={renderResultLegend(data.total_records, isChartCaptureMode)}
                    />
                </RechartsPieChart>
                </ResponsiveContainer>
            </Box>
            </Paper>
        </Box>
        </Box>
    </Box>

    {/* 部署 */}
    <Box sx={{ width: '100%' }}>
        <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChart sx={{ mr: 1 }} /> 部署
        </Typography>
        {departmentChartPages.map((pageData, pageIndex) => (
          <Box
            key={'department-chart-page-' + pageIndex}
            className="department-chart-capture-target"
            data-dashboard-department-page={pageIndex + 1}
            sx={{
              height: Math.max(400, pageData.length * 30),
              mt: pageIndex === 0 ? 0 : 2,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
                layout="vertical"
                data={pageData}
                margin={{ top: 5, right: departmentCountLabelRightMargin, left: 20, bottom: 5 }}
            >
                <XAxis type="number" />
                <YAxis
                type="category"
                dataKey="name"
                interval={0}
                width={departmentAxisLayout.width}
                tick={(
                  <CategoryAxisTick
                    maxCharacters={departmentAxisLayout.maxCharacters}
                  />
                )}
                />
                <Tooltip
                formatter={(value, name, props) => [`${value}件`, '件数']}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid #ccc' }}
                />
                <Bar
                dataKey="value"
                name="件数"
                fill="#8884d8"
                label={renderDepartmentCountLabel}
                >
                {pageData.map((entry, index) => (
                    <Cell key={`dept-bar-${pageIndex}-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
                </Bar>
            </RechartsBarChart>
            </ResponsiveContainer>
          </Box>
        ))}
        </Paper>
    </Box>
    </Box>
);
};

export default OverviewTab;
